import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type React from 'react'
import { useReducedMotion } from '../../hooks/useReducedMotion'

type Tag = 'div' | 'article' | 'p' | 'section'
interface Props { as?: Tag; className?: string; style?: CSSProperties; id?: string; delay?: number; children?: ReactNode; 'aria-label'?: string }

/**
 * Content is visible at rest. Only blocks that start below the fold slide in once, as they enter the screen.
 */
export function Reveal({ as = 'div', className = '', style, delay = 0, children, ...rest }: Props) {
  const ref = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()
  const [pre, setPre] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || reduced || typeof IntersectionObserver === 'undefined') return
    if (el.getBoundingClientRect().top <= window.innerHeight) return
    setPre(true)
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPre(false)
          io.disconnect()
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [reduced])

  const Tag = as as 'div'
  const cls = `rv${pre ? ' pre' : ''}${className ? ` ${className}` : ''}`
  return (
    <Tag ref={ref as React.Ref<HTMLDivElement>} className={cls} style={delay ? { ...style, transitionDelay: `${delay}s` } : style} {...rest}>
      {children}
    </Tag>
  )
}
